// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

import { useState, useEffect, useCallback } from "react"
import { changelogMessages } from "@/data/changelog"
import type { ChangelogMessage } from "@/views/home/component.changelogModal"

const LAST_SEEN_STORAGE_KEY = "lastChangelogMessageSeen"

export const useChangelog = () => {
  const [lastChangelogMessageSeen, setLastChangelogMessageSeen] = useState(
    () => localStorage.getItem(LAST_SEEN_STORAGE_KEY) || "",
  )
  const [showChangelog, setShowChangelog] = useState(false)
  const [currentMessage, setCurrentMessage] = useState<ChangelogMessage | null>(
    null,
  )

  const markChangelogAsSeen = useCallback((messageId: string) => {
    setLastChangelogMessageSeen(messageId)
    localStorage.setItem(LAST_SEEN_STORAGE_KEY, messageId)
  }, [])

  useEffect(() => {
    // Trouver le message le plus récent qui n'a pas été vu
    const latestMessage = changelogMessages[0] // Le premier est le plus récent
    if (latestMessage && latestMessage.id !== lastChangelogMessageSeen) {
      setCurrentMessage(latestMessage)
      setShowChangelog(true)
    }
  }, [lastChangelogMessageSeen])

  const handleMarkAsSeen = () => {
    if (currentMessage) {
      markChangelogAsSeen(currentMessage.id)
      setShowChangelog(false)
      setCurrentMessage(null)
    }
  }

  // US §22 — any close gesture (X, backdrop, Esc) dismisses the modal for
  // good, same as the explicit "Mark as Seen" action; otherwise it would
  // reappear on the very next navigation.
  const handleClose = () => {
    if (currentMessage) {
      markChangelogAsSeen(currentMessage.id)
    }
    setShowChangelog(false)
    setCurrentMessage(null)
  }

  return {
    showChangelog,
    currentMessage,
    handleMarkAsSeen,
    handleClose,
  }
}
